// https://developers.google.com/youtube/iframe_api_reference
// PlayerState
// -1 (unstarted)
// 0 YT.PlayerState.ENDED
// 1 YT.PlayerState.PLAYING
// 2 YT.PlayerState.PAUSED
// 3 YT.PlayerState.BUFFERING
// 5 YT.PlayerState.CUED

import { store, getContext, getElement } from '@wordpress/interactivity';

/**
 * Module-scope, NOT in Interactivity state: the player is an imperative, non-serialisable object.
 *
 * IMPORTANT: `new YT.Player()` returns an object immediately, but its methods
 * (loadVideoById, playVideo, …) only exist after `onReady`. "player exists"
 * therefore does NOT mean "player is usable". `state.ready` (set in onReady)
 * is the only valid gate.
 */
let player         = null;
let pendingId      = null; // a click that arrived before the player was ready
let currentId      = null; // the video we last asked the player to load
let retriedCurrent = false;
let loadTimeout    = null;
let apiPromise     = null;


/**
 * Order-independent loader for the IFrame API. Works whether the API script:
 *  - already finished loading before this module ran (site.js injects it early),
 *  - is still loading, or
 *  - was never injected.
 * The check and the callback assignment happen in the same synchronous task,
 * so the API cannot slip in between them.
 */
function loadYouTubeApi() {
	if ( apiPromise ) {
		return apiPromise;
	}
	apiPromise = new Promise( ( resolve ) => {
		if ( window.YT && window.YT.Player ) {
			resolve( window.YT );
			return;
		}
 
		// Chain, never overwrite, an existing callback.
		const previous = window.onYouTubeIframeAPIReady;
		window.onYouTubeIframeAPIReady = () => {
			if ( typeof previous === 'function' ) {
				previous();
			}
			resolve( window.YT );
		};
 
		// Inject only if nobody else (e.g. assets/site.js) already did.
		if ( ! document.querySelector( 'script[src*="youtube.com/iframe_api"]' ) ) {
			const tag = document.createElement( 'script' );
			tag.src = 'https://www.youtube.com/iframe_api';
			document.head.appendChild( tag );
		}
	} );
	return apiPromise;
}

// Start fetching as early as this module runs.
loadYouTubeApi();

 
const errorDescriptions = {
	2: 'The request contains an invalid parameter value.',
	5: 'The requested content cannot be played in an HTML5 player or another error related to the HTML5 player has occurred.',
	100: 'The video requested was not found. This error occurs when a video has been removed (for any reason) or has been marked as private.',
	101: 'The owner of the requested video does not allow it to be played in embedded players.',
	150: 'This error is the same as 101 i.e. the owner of the requested video does not allow it to be played in embedded players.',
	153: 'The request does not include the HTTP Referer header or equivalent API Client identification.',
};

// Declare the store BEFORE anything that references `state` can run.
// Plain defaults (ready, loading, statusMessage, …) are declared in render.php
// so server-side rendering outputs them; only derived values live here.
const { state } = store( 'youtube-player', {
	state: {
		// NB: despite the name this means "player panel is visible", not "audio is playing".
		get isPlaying() {
			return state.showPlayer;
		},
	},
	actions: {
		play: () => {
			// Existing markup uses `list`; `videoId` is the better name if you ever rename it.
			const { videoId, list } = getContext();
			const id = videoId || list;
			if ( ! id ) {
				return;
			}
 
			state.statusMessage = '';
			state.loading = true;
			pendingId = id;
 
			if ( state.ready ) {
				// Called synchronously inside the click → keeps the user gesture
				// (matters for autoplay policies, especially iOS Safari).
				startPending( false );
				return;
			}
 
			// Not ready yet: remember the click (it is replayed in onReady) and
			// give feedback instead of silently dropping it.
			state.statusMessage = state.i18n.loading;
			clearTimeout( loadTimeout );
			loadTimeout = setTimeout( () => {
				if ( ! state.ready ) {
					pendingId = null;
					state.loading = false;
					state.statusMessage = state.i18n.unavailable;
				}
			}, 15000 ); // API script blocked / offline / very slow
		},
		next: () => {
			if ( state.ready ) {
				player.nextVideo(); // playlist-only
			}
		},
		prev: () => {
			if ( state.ready ) {
				player.previousVideo(); // playlist-only
			}
		},
		closePlayer: () => {
			pendingId = null;
			clearTimeout( revealTimeout );
			if ( state.ready ) {
				player.stopVideo();
			}
			state.showPlayer = false;
			state.loading = false;
			state.statusMessage = '';
		},
	},
	callbacks: {
		// data-wp-init on the block root: runs once the DOM node exists.
		*init() {
			if ( player ) {
				return;
			}
			const { ref } = getElement();
			const mount = ref.querySelector( '#youtube-iframe-player' );
			if ( ! mount ) {
				return;
			}
 
			const YT = yield loadYouTubeApi();
			if ( player ) {
				return;
			}
 
			player = new YT.Player( mount, {
				height: '270',
				width: '480',
				// No `videoId`, no `loop`: loading is done with loadVideoById(),
				// and looping is handled on ENDED below.
				playerVars: {
					autoplay: 1,
					enablejsapi: 1,
					fs: 1,
					rel: 0,
					origin: window.location.origin,
				},
				events: {
					onReady: eventOnReady,
					onStateChange: eventOnStateChange,
					onError: onPlayerError,
				},
			} );
		},
	},
} );

function startPending( deferred ) {
	if ( ! state.ready || ! pendingId ) {
		return;
	}
	currentId = pendingId;
	pendingId = null;
	retriedCurrent = false;
	player.loadVideoById( currentId ); // single video → loadVideoById (loadPlaylist expects a playlist ID)
 
	// A click replayed after onReady is no longer inside the original user
	// gesture; if the browser refuses to autoplay, the visitor must be able to
	// see the player and press YouTube's own play button.
	if ( deferred ) {
		state.showPlayer = true;
	}
 
	// Safety net: if the browser blocks autoplay, YouTube never reports
	// BUFFERING/PLAYING, so the panel would stay hidden and the click would
	// look dead. Reveal it so the visitor can press YouTube's own play button.
	clearTimeout( revealTimeout );
	revealTimeout = setTimeout( () => {
		if ( state.playerState !== YT.PlayerState.PLAYING ) {
			state.loading = false;
			state.showPlayer = true;
		}
	}, 3000 );
}

// Fires when the player's methods become usable.
// This event fires whenever a player has finished loading and is ready to begin receiving API calls. Your application should implement this function if you want to automatically execute certain operations, such as playing the video or displaying information about the video, as soon as the player is ready.
function eventOnReady() {
	clearTimeout( loadTimeout );
	state.ready = true;
	state.statusMessage = '';
	startPending( true ); // replay a click that arrived too early
	if ( ! currentId ) {
		state.loading = false;
	}
}

// This event fires whenever the player's state changes. The data property of the event object that the API passes to your event listener function will specify an integer that corresponds to the new player state.
function eventOnStateChange( event ) {
	state.playerState = event.data;
	const { PLAYING, BUFFERING, ENDED } = YT.PlayerState;
 
	// Showing after BUFFERING avoids a black iframe flash.
	if ( event.data === BUFFERING || event.data === PLAYING ) {
		state.showPlayer = true;
		state.statusMessage = '';
	}
	if ( event.data === PLAYING ) {
		state.loading = false;
	}
	// Replaces `loop: 1` / setLoop(true), which only apply to playlists.
	if ( event.data === ENDED ) {
		player.seekTo( 0, true );
		player.playVideo();
	}
}

function onPlayerError( event ) {
	const code = event.data;
	console.error(
		`YouTube player error (${ code }): ${ errorDescriptions[ code ] || 'Unknown YouTube player error.' }`
	);
 
	// 5 = generic HTML5 player failure: the only code worth one retry.
	if ( code === 5 && currentId && ! retriedCurrent ) {
		retriedCurrent = true;
		setTimeout( () => player.loadVideoById( currentId ), 1500 );
		return;
	}
 
	// Everything else (2, 100, 101, 150, 153, unknown) cannot recover by retrying.
	state.loading = false;
	state.showPlayer = false;
	state.statusMessage = state.i18n.errors[ code ] || state.i18n.errors.default;
}
